import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { assignStaffRoleAndEntity, getRoles } from "src/api/settings";
import { SelectField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useDashboardContext } from "src/context/DashboardContext";
import { showSuccessToast } from "src/lib/utils";
import type { StaffMember } from "src/types/settings";

const validationSchema = Yup.object().shape({
	role_id: Yup.string().required("This field is required"),
	store_or_warehouse_id: Yup.string().required("This field is required"),
});

interface AssignFormValues {
	user_id?: number | string;
	role_id: string;
	store_or_warehouse_id: string;
}

interface EditStaffRoleProps {
	staff: StaffMember;
}

const EditStaffRole = ({ staff }: EditStaffRoleProps) => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();
	const { entities } = useDashboardContext();

	const { data: rawRoles } = useQuery({ queryKey: ["roles"], queryFn: () => getRoles(), suspense: true });

	const { mutate, isLoading } = useMutation(assignStaffRoleAndEntity, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["teams"]);
			closePortal();
		},
	});

	const roleOptions = (rawRoles?.data ?? []).map(({ id, name }) => ({ label: name ?? "Unnamed", value: String(id) }));
	const entityOptions = entities.map(({ id, name }) => ({ label: name, value: String(id) }));

	const initialValues: AssignFormValues = {
		user_id: staff?.id,
		role_id: String(staff?.user_type_id ?? ""),
		store_or_warehouse_id: String(staff?.store_or_warehouse_id ?? ""),
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, setFieldValue, handleSubmit, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<SelectField
						label="Role"
						name="role_id"
						placeholder="Select a role"
						value={values.role_id}
						error={touched.role_id && errors.role_id}
						onChange={(value) => setFieldValue("role_id", value)}
						options={roleOptions}
						emptyMessage="No roles yet"
						required
					/>

					<SelectField
						label="Store / warehouse"
						name="store_or_warehouse_id"
						placeholder="Select store or warehouse"
						value={values.store_or_warehouse_id}
						error={touched.store_or_warehouse_id && errors.store_or_warehouse_id}
						onChange={(value) => setFieldValue("store_or_warehouse_id", value)}
						options={entityOptions}
						emptyMessage="No stores or warehouses yet"
						required
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Saving..." : "Save changes"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default EditStaffRole;
